import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { NavigationRange } from '@/pages/Onboarding/components/NavigationRange';
import {
  OptionItemSquareWithIcon,
  OptionItemSquareWithIconList,
} from '@/pages/Onboarding/components/OptionItemSquareWithIcon/OptionItemSquareWithIcon';
import { useOnboarding } from '@/pages/Onboarding/context';
import { IDEAL_MATCH_OPTIONS } from '@/pages/Onboarding/options/IdealMatch/IDEAL_MATCH_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { OwnAiTitle } from '../../components/Title';
import { useLayoutFormSelections } from '../../hooks/useLayoutFormSelections';

import { IdealMatchStyles } from './IdealMatch.styles';

import type { LayoutDataType } from '../../types';

export const ScreenIdealMatch: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'IdealMatch',
      });
    }
  }, []);

  const { ageGeneration, bodyShape, hairColor } = useLayoutFormSelections();

  React.useEffect(() => {
    preloadImage(
      `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second/${ageGeneration}/${bodyShape}/${hairColor}/personality.jpg`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const ideal_match = form.watch('ideal_match');

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['ideal_match']>,
  ) => {
    form.setValue('ideal_match', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        ideal_match: option.title,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        if (shouldAnimate) {
          setShowElements(false);
        }
        nextStepTimeout(wizard.nextStep);
      }, 200);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  const handleBackStep = (): void => {
    if (shouldAnimate) {
      setShowElements(false);
      nextStepTimeout(() => wizard.goToStep('hair_color'));
    } else {
      wizard.goToStep('hair_color');
    }
  };

  return (
    <IdealMatchStyles background="dark-violet">
      <NavigationRange onBack={handleBackStep} />

      <OwnAiTitle
        animate={shouldAnimate}
        show={showElements}
        className="ideal-match-title"
      >
        What are you looking for?
      </OwnAiTitle>

      <IonContent>
        <OptionItemSquareWithIconList>
          {IDEAL_MATCH_OPTIONS.map((option) => (
            <OptionItemSquareWithIcon
              key={option.id}
              Icon={option.icon}
              isSelected={ideal_match === option.id}
              onClick={() => handleSelect(option)}
              title={option.title}
              TEST_ID={`ideal_match-${option.id}`}
              show={showElements}
              animate={shouldAnimate}
            />
          ))}
        </OptionItemSquareWithIconList>
      </IonContent>
    </IdealMatchStyles>
  );
};
